import { createContext, useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import * as authApi from '@/api/auth'
import { userHasPermission } from './permissions'
import type { Permission, User } from '@/types'

export interface AuthContextValue {
  user: User | null
  token: string | null
  loading: boolean
  login: (username: string, password: string) => Promise<void>
  logout: () => void
  hasPermission: (permission: Permission) => boolean
}

export const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('auth_token'))
  const [loading, setLoading] = useState<boolean>(true)

  const logout = useCallback(() => {
    authApi.logout()
    setToken(null)
    setUser(null)
  }, [])

  // On mount, if token exists, fetch current user to validate it
  useEffect(() => {
    if (!token) {
      setLoading(false)
      return
    }
    authApi
      .me()
      .then((u) => {
        setUser(u)
      })
      .catch(() => {
        // token invalid/expired — clear silently (401 handler also fires, but be safe)
        logout()
      })
      .finally(() => setLoading(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Listen for 401 events emitted by the API client
  useEffect(() => {
    const handler = () => logout()
    window.addEventListener('auth:unauthenticated', handler)
    return () => window.removeEventListener('auth:unauthenticated', handler)
  }, [logout])

  const login = useCallback(async (username: string, password: string) => {
    const res = await authApi.login(username, password)
    localStorage.setItem('auth_token', res.access_token)
    setToken(res.access_token)
    try {
      const u = await authApi.me()
      setUser(u)
    } catch (error) {
      // A token without a matching user context cannot form a valid local session.
      logout()
      throw error
    }
  }, [logout])

  const hasPermission = useCallback(
    (permission: Permission) => userHasPermission(user, permission),
    [user],
  )

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, hasPermission }}>
      {children}
    </AuthContext.Provider>
  )
}
